import React, {
  KeyboardEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

import { createPortal } from 'react-dom'

import { TooltipPanel, TooltipRoot, TooltipTrigger } from './styles'

interface TooltipPosition {
  left: number
  placement: 'top' | 'bottom'
  top: number
}

interface TooltipProps {
  children: ReactNode
  content: ReactNode
  label: string
}

const Tooltip = ({ children, content, label }: TooltipProps) => {
  const tooltipId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<TooltipPosition | null>(null)

  useEffect(() => {
    if (!open) return

    const updatePosition = () => {
      const trigger = triggerRef.current

      if (!trigger) return

      const bounds = trigger.getBoundingClientRect()
      const horizontalMargin = 160

      setPosition({
        left: Math.min(
          Math.max(bounds.left + bounds.width / 2, horizontalMargin),
          window.innerWidth - horizontalMargin,
        ),
        placement: bounds.top >= 180 ? 'top' : 'bottom',
        top: bounds.top >= 180 ? bounds.top : bounds.bottom,
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open])

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const panel =
    open && position
      ? createPortal(
          <TooltipPanel
            id={tooltipId}
            role="tooltip"
            data-redesign-theme={
              triggerRef.current
                ?.closest('[data-redesign-theme]')
                ?.getAttribute('data-redesign-theme') || 'light'
            }
            $left={position.left}
            $placement={position.placement}
            $top={position.top}
          >
            {content}
          </TooltipPanel>,
          triggerRef.current?.closest('main') || document.body,
        )
      : null

  return (
    <TooltipRoot>
      <TooltipTrigger
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((isOpen) => !isOpen)}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
      </TooltipTrigger>
      {panel}
    </TooltipRoot>
  )
}

export default Tooltip
